import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, Download, FileText, TrendingUp, Heart, Shield, Clock, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { hasPaidAccess } from '../utils/accessControl';

const UpgradePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('weekly');
  const [pricing, setPricing] = useState(null);
  const hasAccess = hasPaidAccess(user);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`)
      .then(res => res.ok ? res.json() : null)
      .then(data => setPricing(data))
      .catch(() => {});
  }, []);

  // If user already has access, redirect to dashboard
  if (hasAccess) {
    navigate('/dashboard');
    return null;
  }

  const features = [
    { icon: TrendingUp, text: 'Full blood tracking with trend graphs' },
    { icon: FileText, text: 'Nutrition & deficiency tracking' },
    { icon: Heart, text: 'Medications, supplements, symptoms tracking' },
    { icon: FileText, text: 'Learn & Notes (private journal)' },
    { icon: Download, text: 'Full PDF exports for your healthcare team' },
    { icon: Lock, text: 'Complete integrative health content' },
    { icon: Shield, text: 'All future updates included' }
  ];

  const trustNotes = [
    'Secure payment processing',
    'Cancel anytime — no questions asked',
    'Export all your data anytime',
    'Educational content, not medical advice'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-full text-sm font-medium mb-4 shadow-md">
            <Lock className="h-4 w-4" />
            Upgrade to Companion
          </div>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Full access to tracking, notes, exports, and integrative health
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Everything you need to stay organised, informed, and connected to your health journey.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="bg-white rounded-2xl p-8 md:p-10 border-2 border-blue-200 shadow-xl mb-8">
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => setBillingCycle('weekly')}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                billingCycle === 'weekly'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                billingCycle === 'yearly'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs">Save 24%</span>
            </button>
          </div>

          {/* Price Display */}
          <div className="text-center mb-8">
            <div className="flex items-baseline justify-center gap-2 mb-2">
              <span className="text-5xl font-bold text-slate-900">
                ${billingCycle === 'weekly' ? '2.50' : '99'}
              </span>
              <span className="text-xl text-slate-600">
                USD {billingCycle === 'weekly' ? '/week' : '/year'}
              </span>
            </div>
            {pricing && (
              <p className="text-sm text-slate-400 mb-1" data-testid="upgrade-aud-price">
                ≈ A${billingCycle === 'weekly' ? pricing.weekly_aud?.toFixed(2) : pricing.yearly_aud} AUD
              </p>
            )}
            {billingCycle === 'yearly' && (
              <p className="text-sm text-teal-600 font-medium">
                Under USD $1.91 a week — save 24%
              </p>
            )}
          </div>

          {/* Features List */}
          <div className="space-y-3 mb-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="flex items-start gap-3">
                  <div className="p-1.5 bg-teal-100 rounded-lg flex-shrink-0">
                    <Icon className="h-4 w-4 text-teal-600" />
                  </div>
                  <span className="text-slate-700">{feature.text}</span>
                </div>
              );
            })}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => {
              // TODO: Integrate Stripe checkout
              alert('Stripe checkout integration coming soon. For now, this is a UI/UX demonstration.');
            }}
            className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
          >
            Unlock Companion Access
          </button>

          <p className="text-center text-sm text-slate-600 mt-4">
            Cancel anytime. No lock-in.
          </p>
        </div>

        {/* Trust Notes */}
        <div className="bg-slate-50 rounded-xl p-6 md:p-8 border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-5 w-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">What you should know</h3>
          </div>
          
          <ul className="space-y-3">
            {trustNotes.map((note, index) => (
              <li key={index} className="flex items-start gap-3 text-sm text-slate-700">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom Reassurance */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-600">
            Questions?{' '}
            <button
              onClick={() => navigate('/common-questions')}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              Read our FAQ
            </button>
            {' '}or take your time deciding.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
